/* ================================================================
   FicheSaisieNotesPage.tsx — Fiche de saisie des notes
   V3 : Deux modèles — Standard (6 séquences) & Détaillé (CC/EU/SEQ × 5)
   + Génération automatique d'une fiche PAR enseignant/matière/coef
     de la classe sélectionnée (attributions chargées depuis l'API).
   ================================================================ */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  GraduationCap, Printer, FileSpreadsheet, Download,
  Loader2, Search, Settings, Calendar, Save, Pencil,
  Building2, ChevronDown, ChevronUp, LayoutTemplate, Table2,
  UserRound, BookOpen, Info, X
} from "lucide-react";
import { apiService } from "@/services/api";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";
import { useLang } from "@/hooks/useLang";

// ==================== HELPERS ====================
const extractData = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  if (res && typeof res === 'object') {
    // V3 : version robuste (identique aux listes) — cherche aussi dans les
    // objets imbriques : data.items, data.students... Sans cela, les réponses
    // enveloppées sont mal lues -> élèves manquants.
    const keys = ['data', 'items', 'results', 'students', 'eleves', 'rows'];
    for (const k of keys) {
      if (Array.isArray(res[k])) return res[k];
      if (res[k] && typeof res[k] === 'object' && !Array.isArray(res[k])) {
        for (const k2 of keys) {
          if (Array.isArray(res[k][k2])) return res[k][k2];
        }
      }
    }
  }
  return [];
};

const AUTH_STORAGE_KEY = 'auth-storage';
const SELECTED_YEAR_ID_KEY = 'lybibal-selected-year-id';
const SELECTED_YEAR_LABEL_KEY = 'lybibal-selected-year-label';
const SELECTED_YEAR_EVENT = 'lybibal-year-change';

const getUserSchoolId = (): number | null => {
  try {
    const authRaw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (authRaw) {
      const parsed = JSON.parse(authRaw);
      const state = parsed?.state || parsed;
      if (state?.user?.school_id) return Number(state.user.school_id);
    }
  } catch (e) { console.warn('Failed to get school_id:', e); }
  return null;
};

const getSelectedYearId = (): number | null => {
  try {
    const yearId = localStorage.getItem(SELECTED_YEAR_ID_KEY);
    if (yearId) return Number(yearId);
  } catch (e) { console.warn('Failed to get year_id:', e); }
  return null;
};

/**
 * GET silencieux : contourne l'intercepteur d'erreurs d'apiService pour
 * éviter le toast "Ressource non trouvée" sur les endpoints OPTIONNELS
 * (ex : /teacher-subject-classes peut ne pas exister -> 404 attendu).
 * Une erreur/absence d'endpoint renvoie simplement { data: [] }.
 */
const API_BASE_URL: string =
  (import.meta as any)?.env?.VITE_API_URL ||
  (import.meta as any)?.env?.VITE_API_BASE_URL ||
  '/api/v1';

async function silentGet(url: string, params: Record<string, any> = {}): Promise<any> {
  try {
    // V2 : ce frontend authentifie par COOKIE httpOnly (withCredentials dans
    // api.ts — plus de JWT en localStorage). Sans credentials, chaque appel
    // revenait en 401 silencieux → aucune affectation chargée. On envoie donc
    // les cookies ; le header Bearer reste en repli pour les anciens déploiements.
    const token = localStorage.getItem('auth_token');
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null)
    );
    const qs = new URLSearchParams(cleanParams as Record<string, string>).toString();
    const resp = await fetch(`${API_BASE_URL}${url}${qs ? `?${qs}` : ''}`, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) return { data: [] };
    return await resp.json();
  } catch {
    return { data: [] };
  }
}

function getValue(obj: any, ...keys: string[]): any {
  if (!obj || typeof obj !== 'object') return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
    for (const subKey of Object.keys(obj)) {
      const sub = obj[subKey];
      if (sub && typeof sub === 'object' && !Array.isArray(sub)) {
        if (sub[key] !== undefined && sub[key] !== null) return sub[key];
      }
    }
  }
  return undefined;
}

function getStr(obj: any, ...keys: string[]): string {
  const v = getValue(obj, ...keys);
  return v !== undefined && v !== null ? String(v) : '';
}

function downloadCSV(filename: string, rows: string[][]) {
  const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
}

function downloadExcelData(filename: string, data: any[]) {
  const headers = Object.keys(data[0] || {});
  const rows = [headers, ...data.map(row => headers.map(h => row[h] ?? ''))];
  downloadCSV(filename, rows);
}

// ==================== TYPES ====================
type ModelType = 'simple' | 'detailed';

/** Méta d'une fiche : classe + enseignant + matière + coefficient */
interface FicheMeta {
  class_id?: number;
  class_name?: string;
  teacher: string;
  subject: string;
  coefficient: string;
  /** Pour résoudre le coefficient via /grades/subjects quand il est absent */
  subject_id?: number;
}

interface SchoolHeader {
  republic_fr: string;
  republic_en: string;
  motto_fr: string;
  motto_en: string;
  region: string;
  department: string;
  delegation: string;
  sub_division: string;
  school_name: string;
  school_name_en: string;
  address: string;
  po_box: string;
  logo_url: string;
}

interface Student {
  id: number;
  matricule?: string;
  class_id?: number;
  class_name?: string;
  first_name: string;
  last_name: string;
  gender: string;
  // Modèle simple (6 séquences)
  seq1?: number; seq2?: number; seq3?: number;
  seq4?: number; seq5?: number; seq6?: number;
  // Modèle détaillé (5 séquences × 3)
  cc1?: number; eu1?: number; s1?: number;
  cc2?: number; eu2?: number; s2?: number;
  cc3?: number; eu3?: number; s3?: number;
  cc4?: number; eu4?: number; s4?: number;
  cc5?: number; eu5?: number; s5?: number;
}

function normalizeStudentWithGrades(s: any, classId?: number, className?: string): Student {
  const stu = s.student || s;
  const grades = s.grades || s.student?.grades || s;
  const toNum = (v: any) => (v === undefined || v === null || v === '') ? undefined : (isNaN(Number(v)) ? undefined : Number(v));
  return {
    id: stu.id ?? s.id ?? 0,
    matricule: stu.matricule || s.matricule || undefined,
    class_id: classId ?? s.class_id ?? stu.class_id ?? s.student_class_id ?? undefined,
    class_name: className ?? s.class_name ?? stu.class_name ?? undefined,
    first_name: stu.first_name || stu.prenom || s.first_name || s.prenom || '',
    last_name: stu.last_name || stu.nom || s.last_name || s.nom || '',
    gender: stu.gender || stu.sex || stu.sexe || s.gender || s.sex || s.sexe || '',
    // Simple
    seq1: toNum(grades.seq1 ?? grades.sequence1 ?? grades.s1),
    seq2: toNum(grades.seq2 ?? grades.sequence2 ?? grades.s2),
    seq3: toNum(grades.seq3 ?? grades.sequence3 ?? grades.s3),
    seq4: toNum(grades.seq4 ?? grades.sequence4 ?? grades.s4),
    seq5: toNum(grades.seq5 ?? grades.sequence5 ?? grades.s5),
    seq6: toNum(grades.seq6 ?? grades.sequence6 ?? grades.s6),
    // Détaillé
    cc1: toNum(grades.cc1), eu1: toNum(grades.eu1), s1: toNum(grades.s1 ?? grades.seq1),
    cc2: toNum(grades.cc2), eu2: toNum(grades.eu2), s2: toNum(grades.s2 ?? grades.seq2),
    cc3: toNum(grades.cc3), eu3: toNum(grades.eu3), s3: toNum(grades.s3 ?? grades.seq3),
    cc4: toNum(grades.cc4), eu4: toNum(grades.eu4), s4: toNum(grades.s4 ?? grades.seq4),
    cc5: toNum(grades.cc5), eu5: toNum(grades.eu5), s5: toNum(grades.s5 ?? grades.seq5),
  };
}

function calculateGeneral(s: Student, model: ModelType): number {
  if (model === 'detailed') {
    const vals = [s.s1, s.s2, s.s3, s.s4, s.s5].filter(v => v !== undefined && v !== null && !isNaN(v)) as number[];
    if (vals.length === 0) return 0;
    return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
  }
  const vals = [s.seq1, s.seq2, s.seq3, s.seq4, s.seq5, s.seq6].filter(v => v !== undefined && v !== null && !isNaN(v)) as number[];
  if (vals.length === 0) return 0;
  return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
}

function generateMockStudents(count: number, model: ModelType): Student[] {
  const firstNames = ['Jean','Marie','Pierre','Anne','Paul','Sophie','Michel','Catherine','Robert','Christine'];
  const lastNames = ['Kamga','Fouda','Ndjomo','Tchinda','Mbarga','Essomba','Mba','Ngono','Biya','Muna'];
  const students: Student[] = [];
  for (let i = 1; i <= count; i++) {
    const fn = firstNames[Math.floor(Math.random() * firstNames.length)];
    const ln = lastNames[Math.floor(Math.random() * lastNames.length)];
    const base: Student = {
      id: i, matricule: `MAT-${String(i).padStart(3, '0')}`,
      class_id: (i % 5) + 1,
      first_name: fn, last_name: ln,
      gender: Math.random() > 0.45 ? 'M' : 'F',
    };
    if (model === 'simple') {
      base.seq1 = Math.floor(Math.random() * 6) + 10;
      base.seq2 = Math.floor(Math.random() * 6) + 10;
      base.seq3 = Math.floor(Math.random() * 6) + 10;
      base.seq4 = Math.floor(Math.random() * 6) + 10;
      base.seq5 = Math.floor(Math.random() * 6) + 10;
      base.seq6 = Math.floor(Math.random() * 6) + 10;
    } else {
      base.cc1 = Math.floor(Math.random() * 4) + 8; base.eu1 = Math.floor(Math.random() * 4) + 8; base.s1 = Math.floor(Math.random() * 6) + 10;
      base.cc2 = Math.floor(Math.random() * 4) + 8; base.eu2 = Math.floor(Math.random() * 4) + 8; base.s2 = Math.floor(Math.random() * 6) + 10;
      base.cc3 = Math.floor(Math.random() * 4) + 8; base.eu3 = Math.floor(Math.random() * 4) + 8; base.s3 = Math.floor(Math.random() * 6) + 10;
      base.cc4 = Math.floor(Math.random() * 4) + 8; base.eu4 = Math.floor(Math.random() * 4) + 8; base.s4 = Math.floor(Math.random() * 6) + 10;
      base.cc5 = Math.floor(Math.random() * 4) + 8; base.eu5 = Math.floor(Math.random() * 4) + 8; base.s5 = Math.floor(Math.random() * 6) + 10;
    }
    students.push(base);
  }
  return students.sort((a, b) => a.last_name.localeCompare(b.last_name));
}

// ==================== COMPONENT ====================
export default function FicheSaisieNotesPage() {
  const { lang } = useLang();

  const [schoolId, setSchoolId] = useState<number | null>(getUserSchoolId);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<number | null>(getSelectedYearId);
  const [currentAcademicYear, setCurrentAcademicYear] = useState<{id: number; name: string} | null>(null);

  const [modelType, setModelType] = useState<ModelType>('simple');

  const t = {
    fr: {
      title: 'Fiche de saisie des notes',
      subtitle: 'Saisie des notes par séquence — Année académique',
      listTitle: 'FICHE DE SAISIE DES NOTES',
      configTitle: 'Configuration',
      section: 'Section', cycle: 'Cycle', level: 'Niveau', class: 'Classe',
      allSections: 'Toutes les sections', allCycles: 'Tous les cycles',
      allLevels: 'Tous les niveaux', allClasses: 'Toutes les classes',
      academicYear: 'Année académique',
      teacher: 'Enseignant', subject: 'Matière', coefficient: 'Coef',
      search: 'Rechercher...',
      no: 'N°', name: 'Nom(s) et prénom(s)', sex: 'Sx', matricule: 'Matricule',
      seq1: 'S1', seq2: 'S2', seq3: 'S3', seq4: 'S4', seq5: 'S5', seq6: 'S6',
      general: 'G.',
      save: 'Enregistrer',
      exportPDF: 'PDF', exportExcel: 'Excel', exportExcelData: 'Excel Data',
      noData: 'Aucune donnée', loading: 'Chargement...',
      noYearSelected: 'Aucune année scolaire sélectionnée',
      yearRequired: 'Veuillez sélectionner une année scolaire dans le header',
      saved: 'Notes enregistrées',
      classLabel: 'Classe',
      totalStudents: 'Total élèves',
      headerTitle: "En-tête de l'établissement",
      headerDesc: "Personnalisez l'en-tête qui apparaît sur chaque document",
      schoolName: "Nom de l'établissement",
      schoolNameEn: 'Nom (EN)',
      region: 'Région',
      department: 'Département',
      delegation: 'Délégation',
      subDivision: 'Arrondissement / Sub-Division',
      address: 'Adresse',
      poBox: 'BP / P.O. Box',
      logo: 'Logo',
      saveHeader: "Sauvegarder l'en-tête",
      modelType: 'Modèle de fiche',
      modelSimple: 'Standard (6 séquences)',
      modelDetailed: 'Détaillé CC/EU/SEQ (5 séquences)',
      cc: 'CC', eu: 'EU', seq: 'SEQ',
      // ── Fiches par enseignant ──
      fiches: 'Fiches de la classe',
      fiche: 'Fiche',
      ficheCount: 'fiche(s)',
      loadingAssignments: 'Chargement des enseignants...',
      noAssignments: "Aucune attribution enseignant/matière trouvée pour cette classe. Renseignez manuellement ci-contre (la fiche unique sera générée avec ces valeurs).",
      manualMode: 'Mode manuel',
      autoMode: 'Attributions de la classe',
      popupTitle: 'Information',
      popupClose: 'Fermer',
      popupManual: 'Renseigner manuellement',
    },
    en: {
      title: 'Grade Entry Form',
      subtitle: 'Grade entry by sequence — Academic year',
      listTitle: 'GRADE ENTRY FORM',
      configTitle: 'Configuration',
      section: 'Section', cycle: 'Cycle', level: 'Level', class: 'Class',
      allSections: 'All sections', allCycles: 'All cycles',
      allLevels: 'All levels', allClasses: 'All classes',
      academicYear: 'Academic year',
      teacher: 'Teacher', subject: 'Subject', coefficient: 'Coef.',
      search: 'Search...',
      no: 'No.', name: 'Name(s) and first name(s)', sex: 'Sex', matricule: 'ID',
      seq1: 'S1', seq2: 'S2', seq3: 'S3', seq4: 'S4', seq5: 'S5', seq6: 'S6',
      general: 'Avg.',
      save: 'Save',
      exportPDF: 'PDF', exportExcel: 'Excel', exportExcelData: 'Excel Data',
      noData: 'No data', loading: 'Loading...',
      noYearSelected: 'No academic year selected',
      yearRequired: 'Please select an academic year in the header',
      saved: 'Grades saved',
      classLabel: 'Class',
      totalStudents: 'Total students',
      headerTitle: 'School Header',
      headerDesc: 'Customize the header that appears on each document',
      schoolName: 'School Name',
      schoolNameEn: 'Name (EN)',
      region: 'Region',
      department: 'Department',
      delegation: 'Delegation',
      subDivision: 'Sub Division',
      address: 'Address',
      poBox: 'P.O. Box',
      logo: 'Logo',
      saveHeader: 'Save Header',
      modelType: 'Form model',
      modelSimple: 'Standard (6 sequences)',
      modelDetailed: 'Detailed CC/EU/SEQ (5 sequences)',
      cc: 'CC', eu: 'EU', seq: 'SEQ',
      // ── Per-teacher forms ──
      fiches: 'Class forms',
      fiche: 'Form',
      ficheCount: 'form(s)',
      loadingAssignments: 'Loading teachers...',
      noAssignments: 'No teacher/subject assignment found for this class. Fill in manually on the left (a single form will be generated with these values).',
      manualMode: 'Manual mode',
      autoMode: 'Class assignments',
      popupTitle: 'Info',
      popupClose: 'Close',
      popupManual: 'Fill in manually',
    }
  }[lang];

  const [sections, setSections] = useState<any[]>([]);
  const [cycles, setCycles] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [years, setYears] = useState<any[]>([]);

  const [selectedSection, setSelectedSection] = useState<string>("");
  const [selectedCycle, setSelectedCycle] = useState<string>("");
  const [selectedLevel, setSelectedLevel] = useState<string>("");
  const [selectedClass, setSelectedClass] = useState<string>("");

  const [teacher, setTeacher] = useState('');
  const [subject, setSubject] = useState('');
  const [coefficient, setCoefficient] = useState('');
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [refLoading, setRefLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editing, setEditing] = useState(false);

  // ── Attributions enseignant/matière/coef de la classe sélectionnée ──
  const [assignments, setAssignments] = useState<FicheMeta[]>([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [activeFiche, setActiveFiche] = useState(0);
  // Fiches décochées (exclues de la génération PDF/CSV/Excel).
  // Par défaut TOUT est coché.
  const [excludedKeys, setExcludedKeys] = useState<Set<string>>(new Set());

  const [showNoAssignPopup, setShowNoAssignPopup] = useState(false);

  const [showHeaderConfig, setShowHeaderConfig] = useState(false);
  const [schoolHeader, setSchoolHeader] = useState<SchoolHeader>({
    republic_fr: 'RÉPUBLIQUE DU CAMEROUN',
    republic_en: 'REPUBLIC OF CAMEROON',
    motto_fr: 'Paix-Travail-Patrie',
    motto_en: 'Peace-Work-Fatherland',
    region: "RÉGION DE L'OUEST",
    department: 'DÉPARTEMENT DE LA MIFI',
    delegation: 'DÉLÉGATION DÉPARTEMENTALE DES ENSEIGNEMENTS SECONDAIRES',
    sub_division: 'ARRONDISSEMENT DE BAFOUSSAM II',
    school_name: 'LYCÉE BILINGUE DE BALENG',
    school_name_en: 'GBHS BALENG',
    address: 'BP : 1041 Baleng',
    po_box: 'P.O. Box : 1041 Baleng',
    logo_url: '',
  });

  const getFilterParams = useCallback(() => {
    const params: Record<string, any> = {};
    if (schoolId) params.school_id = schoolId;
    if (selectedAcademicYearId) params.academic_year_id = selectedAcademicYearId;
    return params;
  }, [schoolId, selectedAcademicYearId]);

  useEffect(() => {
    const loadConfig = async () => {
      if (!schoolId || !selectedAcademicYearId) return;
      setRefLoading(true);
      try {
        const filterParams = getFilterParams();
        const [yearRes, secRes, cycRes, levRes] = await Promise.all([
          apiService.get(`/schools/${schoolId}/academic-years`).catch(() => ({ data: [] })),
          apiService.get('/sections', { params: filterParams }).catch(() => ({ data: [] })),
          apiService.get('/cycles', { params: filterParams }).catch(() => ({ data: [] })),
          apiService.get('/levels', { params: filterParams }).catch(() => ({ data: [] })),
        ]);
        setYears(extractData(yearRes));
        setSections(extractData(secRes));
        setCycles(extractData(cycRes));
        setLevels(extractData(levRes));
      } catch (e) {
        console.error('Config load error:', e);
      } finally {
        setRefLoading(false);
      }
    };
    loadConfig();
  }, [schoolId, selectedAcademicYearId, getFilterParams]);

  const loadClasses = useCallback(async () => {
    if (!schoolId || !selectedAcademicYearId || !selectedLevel) {
      setClasses([]);
      setSelectedClass("");
      return;
    }
    try {
      const params: Record<string, any> = { ...getFilterParams(), level_id: parseInt(selectedLevel) };
      const res = await apiService.get('/classes', { params });
      let data = extractData(res);
      const levelIdNum = parseInt(selectedLevel);
      data = data.filter((c: any) => {
        const cid = c.level_id != null ? parseInt(c.level_id) : (c.levelId != null ? parseInt(c.levelId) : null);
        return cid === levelIdNum;
      });
      setClasses(data);
      if (selectedClass && !data.find((c: any) => String(c.id) === selectedClass)) {
        setSelectedClass("");
      }
    } catch (e: any) {
      console.error('Classes load error:', e);
      setClasses([]);
      setSelectedClass("");
    }
  }, [schoolId, selectedAcademicYearId, selectedLevel, getFilterParams]);

  useEffect(() => { loadClasses(); }, [loadClasses]);

  // ─────────────────────────────────────────────────────────────────
  // CHARGEMENT DES ATTRIBUTIONS enseignant ↔ matière ↔ coef
  // pour la classe sélectionnée. C'est ce qui permet de générer
  // UNE fiche par enseignant au lieu d'une seule fiche « Enseignant : — ».
  // Ordre des sources :
  //   1) /teacher-subject-classes?class_id=X   (endpoint dédié)
  //   2) /teachers?class_id=X + parsing de teacher_subject_classes
  // ─────────────────────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────
  // CHARGEMENT DES ATTRIBUTIONS enseignant ↔ matière ↔ coef.
  // Source 1 : /teacher-subject-classes?class_id=X
  // Source 2 : /teachers?class_id=X + parsing de teacher_subject_classes
  // silentGet contourne l'intercepteur -> aucun toast "Ressource non trouvée".
  // ─────────────────────────────────────────────────────────────────
  const fetchClassAssignments = useCallback(async (classId: number): Promise<FicheMeta[]> => {
    const found: FicheMeta[] = [];

    const teacherName = (obj: any): string => {
      const full = getStr(obj, 'teacher_name', 'teacher_full_name', 'full_name', 'enseignant', 'name');
      if (full) return full.trim();
      const last = getStr(obj, 'teacher_last_name', 'last_name', 'nom', 'teacher_nom');
      const first = getStr(obj, 'teacher_first_name', 'first_name', 'prenom', 'teacher_prenom');
      return `${last} ${first}`.trim();
    };
    const subjectName = (obj: any): string =>
      getStr(obj, 'subject_name', 'subject_label', 'subject', 'matiere', 'label_fr', 'name');
    const coefOf = (obj: any): string => {
      const c = getValue(obj, 'coefficient', 'coef');
      return c !== undefined && c !== null && c !== '' ? String(c) : '';
    };

    // 1) Endpoint dédié
    try {
      const res = await silentGet('/teacher-subject-classes', { class_id: classId });
      const arr = extractData(res);
      for (const a of arr) {
        const f: FicheMeta = { teacher: teacherName(a), subject: subjectName(a), coefficient: coefOf(a) };
        if (f.teacher || f.subject) found.push(f);
      }
    } catch { /* endpoint absent → fallback */ }

    // 2) Affectations de l'emploi du temps — SOURCE FIABLE pour le 1er
    //    cycle (5ème…) où teacher_subject_classes est souvent vide : c'est
    //    le même endpoint que l'onglet Affectations (filtre class_id réel).
    if (found.length === 0) {
      try {
        const params: Record<string, any> = { class_id: classId };
        if (selectedAcademicYearId) params.academic_year_id = selectedAcademicYearId;
        const res = await silentGet('/timetable/assignments', params);
        const arr = extractData(res);
        for (const a of arr) {
          const sid = getValue(a, 'subject_id');
          const f: FicheMeta = {
            teacher: getStr(a, 'teacher_name', 'teacher_full_name', 'teacher'),
            subject: subjectName(a) || getStr(a, 'subject_code'),
            coefficient: coefOf(a),
            subject_id: sid != null ? Number(sid) : undefined,
          };
          if (f.teacher || f.subject) found.push(f);
        }
      } catch { /* source suivante */ }
    }

    // 3) Fallback via /teachers?class_id=
    if (found.length === 0) {
      try {
        const res = await silentGet('/teachers', { class_id: classId, school_id: schoolId ?? undefined });
        const arr = extractData(res);
        for (const tchr of arr) {
          const name = teacherName(tchr);
          let subs: any[] = [];
          const raw = tchr.teacher_subject_classes ?? tchr.subject_classes ?? tchr.subjects;
          if (typeof raw === 'string') {
            try { const parsed = JSON.parse(raw); subs = Array.isArray(parsed) ? parsed : []; } catch { subs = []; }
          } else if (Array.isArray(raw)) {
            subs = raw;
          }
          let pushed = false;
          for (const s of subs) {
            const sClass = getValue(s, 'class_id', 'classId');
            if (sClass != null && Number(sClass) !== classId) continue;
            const f: FicheMeta = {
              teacher: getStr(s, 'teacher_name', 'teacher_full_name') || name,
              subject: subjectName(s),
              coefficient: coefOf(s),
            };
            if (f.subject || f.teacher) { found.push(f); pushed = true; }
          }
          if (!pushed) {
            const subj = subjectName(tchr);
            if (subj) found.push({ teacher: name, subject: subj, coefficient: coefOf(tchr) });
          }
        }
      } catch { /* ignore */ }
    }

    // 4) Résolution du COEFFICIENT via /grades/subjects (subject-specialties :
    //    coef spécifique de la classe/série) quand l'affectation n'en porte pas.
    const needCoef = found.filter(f => !f.coefficient && (f.subject_id || f.subject));
    if (needCoef.length > 0) {
      try {
        const res = await silentGet('/grades/subjects', { class_id: classId });
        const subs = extractData(res);
        const byId = new Map<number, string>();
        const byName = new Map<string, string>();
        for (const s of subs) {
          const c = getValue(s, 'coefficient', 'coef');
          const cs = c !== undefined && c !== null && c !== '' ? String(c) : '';
          if (!cs) continue;
          if (s.id != null) byId.set(Number(s.id), cs);
          const nm = String(s.name || s.label_fr || '').trim().toLowerCase();
          if (nm) byName.set(nm, cs);
        }
        for (const f of needCoef) {
          f.coefficient = (f.subject_id ? byId.get(Number(f.subject_id)) : undefined)
            ?? byName.get(f.subject.toLowerCase().trim())
            ?? f.coefficient;
        }
      } catch { /* le coef restera vide */ }
    }

    // Déduplication (même enseignant + même matière)
    const seen = new Set<string>();
    return found.filter(f => {
      const key = `${f.teacher.toLowerCase()}|${f.subject.toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [schoolId, selectedAcademicYearId]);

  // Charge les attributions de TOUTES les classes fournies (niveau entier
  // ou classe unique si une classe est sélectionnée). Chaque fiche porte
  // son class_id/class_name → la génération produit une fiche par
  // (classe, enseignant, matière) du niveau, sans choisir chaque classe.
  const loadAssignmentsForClasses = useCallback(async (classList: Array<{ id: number; name?: string }>) => {
    setAssignmentsLoading(true);
    setAssignments([]);
    setActiveFiche(0);
    const found: FicheMeta[] = [];
    for (const cls of classList) {
      const metas = await fetchClassAssignments(cls.id);
      for (const m of metas) {
        found.push({ ...m, class_id: cls.id, class_name: cls.name || '' });
      }
    }
    // Déduplication (classe + enseignant + matière)
    const seen = new Set<string>();
    const deduped = found.filter(f => {
      const key = `${f.class_id}|${f.teacher.toLowerCase()}|${f.subject.toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    setAssignments(deduped);
    setAssignmentsLoading(false);
    setExcludedKeys(new Set()); // nouveau niveau/classe → tout recoché
  }, [fetchClassAssignments]);

  const ficheKey = useCallback((f: FicheMeta) => `${f.class_id ?? ''}|${f.teacher}|${f.subject}`, []);

  const toggleFiche = useCallback((f: FicheMeta) => {
    const k = ficheKey(f);
    setExcludedKeys(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  }, [ficheKey]);

  useEffect(() => {
    if (selectedClass) {
      // Une classe précise → ses seules fiches
      const cls = classes.find((c: any) => String(c.id) === selectedClass);
      loadAssignmentsForClasses(cls ? [{ id: cls.id, name: cls.name }] : []);
    } else if (classes.length > 0) {
      // ✅ AUCUNE classe choisie → toutes les classes du NIVEAU sélectionné
      loadAssignmentsForClasses(classes.map((c: any) => ({ id: c.id, name: c.name })));
    } else {
      setAssignments([]);
      setActiveFiche(0);
      setShowNoAssignPopup(false);
    }
  }, [selectedClass, classes, loadAssignmentsForClasses]);

  // Popup d'information quand la classe n'a AUCUNE attribution
  // enseignant/matière (ouverture automatique, refermable).
  useEffect(() => {
    if (selectedClass && !assignmentsLoading && assignments.length === 0) {
      setShowNoAssignPopup(true);
    } else {
      setShowNoAssignPopup(false);
    }
  }, [selectedClass, assignmentsLoading, assignments.length]);

  // Liste des fiches à générer : attributions de la classe si présentes,
  // sinon fiche unique avec les valeurs manuelles saisies dans le panneau gauche.
  const getFiches = useCallback((): FicheMeta[] => {
    if (assignments.length > 0) return assignments;
    const cls = selectedClass ? classes.find((c: any) => String(c.id) === selectedClass) : null;
    return [{
      class_id: cls?.id,
      class_name: cls?.name || '',
      teacher: teacher.trim(),
      subject: subject.trim(),
      coefficient: coefficient.trim(),
    }];
  }, [assignments, teacher, subject, coefficient, selectedClass, classes]);

  const fiches = useMemo(() => getFiches(), [getFiches]);
  // ✅ Seules les fiches COCHÉES sont générées (PDF / CSV / Excel)
  const selectedFiches = useMemo(
    () => fiches.filter(f => !excludedKeys.has(ficheKey(f))),
    [fiches, excludedKeys, ficheKey]
  );
  const activeMeta: FicheMeta = fiches[Math.min(activeFiche, fiches.length - 1)] ?? { teacher: '', subject: '', coefficient: '' };

  // V3 : chargement PAR CLASSE d'abord — l'endpoint global /students est
  // PAGINE (20 par defaut) et tronquait la liste ("tous les eleves ne
  // s'affichent pas"). La source par classe garantit class_id + effectif
  // complet. Le global ne sert que de secours. FINI le mock : on affiche la
  // realite (meme vide) pour ne pas masquer les problemes API.
  const loadAllStudents = useCallback(async () => {
    if (!schoolId || !selectedAcademicYearId) return;
    setLoading(true);
    setAllStudents([]); // purge immédiate — plus d'élèves d'un ancien niveau
    try {
      let all: Student[] = [];
      let perClassCount = 0;

      if (classes.length > 0) {
        const promises = classes.map(async (cls: any) => {
          try {
            const res = await apiService.get(`/classes/${cls.id}/students`, { params: getFilterParams() });
            const arr = extractData(res);
            perClassCount += arr.length;
            return arr.map((s: any) => normalizeStudentWithGrades(s, cls.id, cls.name));
          } catch (e) {
            console.warn(`[FicheNotes] Échec chargement classe ${cls.id} (${cls.name}):`, e);
            return [];
          }
        });
        const results = await Promise.all(promises);
        all = results.flat();
      }

      // Secours : endpoint global (pagine) seulement si le par-classe est vide
      if (all.length === 0) {
        try {
          const res = await apiService.get('/students', { params: { ...getFilterParams(), per_page: 500 } });
          const arr = extractData(res);
          if (arr.length > 0) {
            console.warn('[FicheNotes] Fallback global /students utilise (par-classe vide) - liste possiblement tronquee a 500');
            all = arr.map((s: any) => normalizeStudentWithGrades(s));
          }
        } catch { /* ignore */ }
      }

      if (all.length === 0) {
        console.warn('[FicheNotes] AUCUN eleve charge - verifiez la console et les endpoints API');
      }
      setAllStudents(all.sort((a, b) => a.last_name.localeCompare(b.last_name)));
    } finally {
      setLoading(false);
    }
  }, [schoolId, selectedAcademicYearId, classes, getFilterParams]);

  // ✅ Recharger les élèves à CHAQUE changement de liste de classes (niveau).
  // Avant : allStudents n'était chargé que si vide → après un changement de
  // niveau, les fiches du nouveau niveau pointaient vers des classes dont
  // les élèves n'étaient jamais chargés (tableaux vides dans le PDF).
  useEffect(() => {
    if (classes.length > 0) {
      loadAllStudents();
    } else {
      setAllStudents([]);
    }
  }, [classes, loadAllStudents]);

  // V3 : plus de regeneration de mocks au changement de modèle — les donnees
  // reelles sont conservees telles quelles (les deux modeles lisent les memes
  // champs seq/cc/eu/s).

  const displayStudents = useMemo(() => {
    let result = [...allStudents];
    if (selectedClass) {
      result = result.filter(s => s.class_id === parseInt(selectedClass));
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(s =>
        `${s.last_name} ${s.first_name}`.toLowerCase().includes(term) ||
        (s.matricule || '').toLowerCase().includes(term)
      );
    }
    return result;
  }, [allStudents, selectedClass, searchTerm]);

  const clsName = classes.find((c: any) => String(c.id) === selectedClass)?.name || '';
  const yearLabel = years.find((y: any) => y.id === selectedAcademicYearId)?.label || years.find((y: any) => y.id === selectedAcademicYearId)?.name || currentAcademicYear?.name || '2025-2026';

  const updateGrade = (id: number, field: keyof Student, value: string) => {
    const num = parseFloat(value);
    setAllStudents(prev => prev.map(s => s.id === id ? { ...s, [field]: isNaN(num) ? undefined : num } : s));
  };

  const handleSave = () => {
    toast.success(t.saved);
    setEditing(false);
  };

  const handleSaveHeader = () => {
    localStorage.setItem('lybibal-school-header', JSON.stringify(schoolHeader));
    toast.success(lang === 'fr' ? 'En-tête sauvegardé' : 'Header saved');
  };

  // ─── En-têtes de colonnes selon le modèle (réutilisés CSV + impression) ───
  const simpleHeaders = [t.no, t.matricule, t.sex, t.name, t.seq1, t.seq2, t.seq3, t.seq4, t.seq5, t.seq6];
  const detailedHeaders = [
    t.matricule, t.sex, t.name,
    `${t.cc}1`, `${t.eu}1`, `${t.seq}1`,
    `${t.cc}2`, `${t.eu}2`, `${t.seq}2`,
    `${t.cc}3`, `${t.eu}3`, `${t.seq}3`,
    `${t.cc}4`, `${t.eu}4`, `${t.seq}4`,
    `${t.cc}5`, `${t.eu}5`, `${t.seq}5`,
    t.general,
  ];

  const simpleRow = (s: Student, i: number): string[] => [
    String(i + 1), s.matricule || '', s.gender || '', `${s.last_name} ${s.first_name}`,
    String(s.seq1 ?? ''), String(s.seq2 ?? ''), String(s.seq3 ?? ''),
    String(s.seq4 ?? ''), String(s.seq5 ?? ''), String(s.seq6 ?? ''),
  ];

  const detailedRow = (s: Student): string[] => [
    s.matricule || '', s.gender || '', `${s.last_name} ${s.first_name}`,
    String(s.cc1 ?? ''), String(s.eu1 ?? ''), String(s.s1 ?? ''),
    String(s.cc2 ?? ''), String(s.eu2 ?? ''), String(s.s2 ?? ''),
    String(s.cc3 ?? ''), String(s.eu3 ?? ''), String(s.s3 ?? ''),
    String(s.cc4 ?? ''), String(s.eu4 ?? ''), String(s.s4 ?? ''),
    String(s.cc5 ?? ''), String(s.eu5 ?? ''), String(s.s5 ?? ''),
    String(calculateGeneral(s, modelType)),
  ];

  // ─── EXPORT CSV : une SECTION par fiche (enseignant/matière/coef) ───
  const handleExportCSV = () => {
    const allRows: string[][] = [];
    selectedFiches.forEach((f, idx) => {
      const ficheStudents = f.class_id
        ? allStudents.filter(s => s.class_id === f.class_id)
        : displayStudents;
      allRows.push([`${t.fiche} ${idx + 1}/${selectedFiches.length}`]);
      allRows.push([t.classLabel, f.class_name || clsName || '—', t.teacher, f.teacher || '—', t.subject, f.subject || '—', t.coefficient, f.coefficient || '—', t.academicYear, yearLabel]);
      allRows.push([]);
      allRows.push(modelType === 'detailed' ? detailedHeaders : simpleHeaders);
      if (modelType === 'detailed') {
        ficheStudents.forEach((s) => allRows.push(detailedRow(s)));
      } else {
        ficheStudents.forEach((s, i) => allRows.push(simpleRow(s, i)));
      }
      if (idx < selectedFiches.length - 1) allRows.push([]); // ligne vide entre fiches
    });
    downloadCSV(`fiche-saisie-${clsName || 'all'}-${modelType}.csv`, allRows);
    toast.success(`${fiches.length} fiche(s) exportée(s)`);
  };

  const handleExportExcelData = () => {
    const allRows: string[][] = [];
    selectedFiches.forEach((f, idx) => {
      const ficheStudents = f.class_id
        ? allStudents.filter(s => s.class_id === f.class_id)
        : displayStudents;
      allRows.push([`${t.fiche} ${idx + 1}/${selectedFiches.length} — ${f.class_name || clsName || '—'} — ${f.teacher || '—'} — ${f.subject || '—'} — ${t.coefficient}: ${f.coefficient || '—'}`]);
      allRows.push([]);
      allRows.push(modelType === 'detailed' ? detailedHeaders : simpleHeaders);
      if (modelType === 'detailed') {
        ficheStudents.forEach((s) => allRows.push(detailedRow(s)));
      } else {
        ficheStudents.forEach((s, i) => allRows.push(simpleRow(s, i)));
      }
      if (idx < selectedFiches.length - 1) allRows.push([]);
    });
    downloadCSV(`fiche-saisie-data-${clsName || 'all'}-${modelType}.csv`, allRows);
    toast.success('Excel Data exporté');
  };

  // ─── HTML du tableau pour l'impression ───
  const buildSimpleTableHTML = (list: Student[]): string => {
    let rowsHTML = '';
    list.forEach((s, i) => {
      const bg = i % 2 === 0 ? '#fff' : '#f9fafb';
      rowsHTML += `<tr style="background:${bg};">
        <td style="border:1px solid #ddd;padding:3px 5px;text-align:center;font-size:9px;">${i + 1}</td>
        <td style="border:1px solid #ddd;padding:3px 5px;font-size:9px;font-family:monospace;">${s.matricule || '—'}</td>
        <td style="border:1px solid #ddd;padding:3px 5px;text-align:center;font-size:9px;">${s.gender || ''}</td>
        <td style="border:1px solid #ddd;padding:3px 5px;font-size:9px;font-weight:600;">${s.last_name} ${s.first_name}</td>
        <td style="border:1px solid #ddd;padding:3px 5px;text-align:center;font-size:9px;">${s.seq1 ?? ''}</td>
        <td style="border:1px solid #ddd;padding:3px 5px;text-align:center;font-size:9px;">${s.seq2 ?? ''}</td>
        <td style="border:1px solid #ddd;padding:3px 5px;text-align:center;font-size:9px;">${s.seq3 ?? ''}</td>
        <td style="border:1px solid #ddd;padding:3px 5px;text-align:center;font-size:9px;">${s.seq4 ?? ''}</td>
        <td style="border:1px solid #ddd;padding:3px 5px;text-align:center;font-size:9px;">${s.seq5 ?? ''}</td>
        <td style="border:1px solid #ddd;padding:3px 5px;text-align:center;font-size:9px;">${s.seq6 ?? ''}</td>
      </tr>`;
    });
    return `<table style="width:100%;border-collapse:collapse;font-size:9px;">
      <thead>
        <tr>
          <th style="border:1px solid #ddd;background:#1e3a5f;color:#fff;padding:5px;text-align:center;">${t.no}</th>
          <th style="border:1px solid #ddd;background:#1e3a5f;color:#fff;padding:5px;text-align:center;">${t.matricule}</th>
          <th style="border:1px solid #ddd;background:#1e3a5f;color:#fff;padding:5px;text-align:center;">${t.sex}</th>
          <th style="border:1px solid #ddd;background:#1e3a5f;color:#fff;padding:5px;text-align:center;">${t.name}</th>
          <th style="border:1px solid #ddd;background:#1e3a5f;color:#fff;padding:5px;text-align:center;">${t.seq1}</th>
          <th style="border:1px solid #ddd;background:#1e3a5f;color:#fff;padding:5px;text-align:center;">${t.seq2}</th>
          <th style="border:1px solid #ddd;background:#1e3a5f;color:#fff;padding:5px;text-align:center;">${t.seq3}</th>
          <th style="border:1px solid #ddd;background:#1e3a5f;color:#fff;padding:5px;text-align:center;">${t.seq4}</th>
          <th style="border:1px solid #ddd;background:#1e3a5f;color:#fff;padding:5px;text-align:center;">${t.seq5}</th>
          <th style="border:1px solid #ddd;background:#1e3a5f;color:#fff;padding:5px;text-align:center;">${t.seq6}</th>
        </tr>
      </thead>
      <tbody>${rowsHTML}</tbody>
    </table>`;
  };

  const buildDetailedTableHTML = (list: Student[]): string => {
    let rowsHTML = '';
    list.forEach((s, i) => {
      const g = calculateGeneral(s, modelType);
      const bg = i % 2 === 0 ? '#fff' : '#f9fafb';
      const cell = (v: number | undefined, bold: boolean) =>
        `<td style="border:1px solid #ddd;padding:3px 5px;text-align:center;font-size:8px;${bold ? 'font-weight:bold;' : ''}">${v ?? ''}</td>`;
      const seqs = [1, 2, 3, 4, 5].map(n => ({
        cc: s[`cc${n}` as keyof Student] as number | undefined,
        eu: s[`eu${n}` as keyof Student] as number | undefined,
        sq: s[`s${n}` as keyof Student] as number | undefined,
      }));
      rowsHTML += `<tr style="background:${bg};">
        <td style="border:1px solid #ddd;padding:3px 5px;text-align:center;font-size:8px;">${s.matricule || '—'}</td>
        <td style="border:1px solid #ddd;padding:3px 5px;text-align:center;font-size:8px;">${s.gender || ''}</td>
        <td style="border:1px solid #ddd;padding:3px 5px;font-size:8px;font-weight:600;">${s.last_name} ${s.first_name}</td>
        ${seqs.map(q => cell(q.cc, false) + cell(q.eu, false) + cell(q.sq, true)).join('')}
        <td style="border:1px solid #ddd;padding:3px 5px;text-align:center;font-size:8px;font-weight:bold;color:#1e3a5f;">${g}</td>
      </tr>`;
    });
    const thMain = `<th rowspan="2" style="border:1px solid #ddd;background:#1e3a5f;color:#fff;padding:4px;text-align:center;font-size:8px;">`;
    const thSub = `<th style="border:1px solid #ddd;background:#2d4a6f;color:#fff;padding:3px;text-align:center;font-size:7px;">`;
    const seqGroup = (n: number) => `<th colspan="3" style="border:1px solid #ddd;background:#1e3a5f;color:#fff;padding:4px;text-align:center;font-size:8px;">${t.seq}${n}</th>`;
    return `<table style="width:100%;border-collapse:collapse;font-size:8px;">
      <thead>
        <tr>
          ${thMain}${t.matricule}</th>
          ${thMain}${t.sex}</th>
          ${thMain}${t.name}</th>
          ${seqGroup(1)}${seqGroup(2)}${seqGroup(3)}${seqGroup(4)}${seqGroup(5)}
          ${thMain}${t.general}</th>
        </tr>
        <tr>
          ${[1, 2, 3, 4, 5].map(n => `${thSub}${t.cc}${n}</th>${thSub}${t.eu}${n}</th>${thSub}${t.seq}${n}</th>`).join('')}
        </tr>
      </thead>
      <tbody>${rowsHTML}</tbody>
    </table>`;
  };

  // ─── IMPRESSION PDF : UNE PAGE PAR FICHE (enseignant/matière/coef) ───
  const handlePrintPDF = () => {
    if (selectedFiches.length === 0) {
      toast.warning(lang === 'fr' ? 'Cochez au moins une fiche à générer' : 'Check at least one form to generate');
      return;
    }
    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast.error('Autorisez les popups'); return; }
    const today = new Date().toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB');

    const logoHtml = schoolHeader.logo_url
      ? `<img src="${schoolHeader.logo_url}" style="width:55px;height:55px;object-fit:contain;" />`
      : `<div style="width:50px;height:50px;background:#1e3a5f;border-radius:50%;display:flex;align-items:center;justify-content:center;"><span style="color:#fff;font-size:14px;font-weight:bold;">LB</span></div>`;

    const headerBlock = `
      <div style="text-align:center;border-bottom:2px solid #1e3a5f;padding-bottom:8px;margin-bottom:12px;">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;">
          <div style="flex:1;text-align:left;">
            <p style="margin:0;font-size:11px;font-weight:bold;color:#1e3a5f;text-transform:uppercase;">${schoolHeader.republic_fr}</p>
            <p style="margin:2px 0;font-size:9px;color:#555;">${schoolHeader.motto_fr}</p>
            <p style="margin:4px 0 1px 0;font-size:9px;color:#333;">${schoolHeader.region}</p>
            <p style="margin:1px 0;font-size:9px;color:#333;">${schoolHeader.department}</p>
            <p style="margin:1px 0;font-size:9px;color:#333;">${schoolHeader.delegation}</p>
            <p style="margin:1px 0;font-size:9px;color:#333;">${schoolHeader.sub_division}</p>
          </div>
          <div style="width:70px;text-align:center;margin:0 12px;flex-shrink:0;">${logoHtml}</div>
          <div style="flex:1;text-align:right;">
            <p style="margin:0;font-size:11px;font-weight:bold;color:#1e3a5f;text-transform:uppercase;">${schoolHeader.republic_en}</p>
            <p style="margin:2px 0;font-size:9px;color:#555;">${schoolHeader.motto_en}</p>
            <p style="margin:4px 0 1px 0;font-size:9px;color:#333;">${headerEnRegion(schoolHeader.region)}</p>
            <p style="margin:1px 0;font-size:9px;color:#333;">${headerEnDept(schoolHeader.department)}</p>
            <p style="margin:1px 0;font-size:9px;color:#333;">${headerEnDelegation(schoolHeader.delegation)}</p>
            <p style="margin:1px 0;font-size:9px;color:#333;">${schoolHeader.sub_division.replace("ARRONDISSEMENT", "SUB DIVISION")}</p>
          </div>
        </div>
        <div style="margin-top:8px;">
          <p style="margin:0;font-size:14px;font-weight:bold;color:#1e3a5f;">${schoolHeader.school_name}</p>
          <p style="margin:2px 0;font-size:11px;font-weight:600;color:#1e3a5f;">${schoolHeader.school_name_en}</p>
          <p style="margin:2px 0;font-size:9px;color:#666;">${schoolHeader.address} | ${schoolHeader.po_box}</p>
        </div>
      </div>`;

    // Élèves de la fiche : la classe de la fiche si connue, sinon la classe
    // sélectionnée (fiche manuelle) ; le terme de recherche s'applique aussi.
    const studentsForFiche = (fiche: FicheMeta): Student[] => {
      let list = fiche.class_id
        ? allStudents.filter(s => s.class_id === fiche.class_id)
        : [...displayStudents];
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        list = list.filter(s =>
          `${s.last_name} ${s.first_name}`.toLowerCase().includes(term) ||
          (s.matricule || '').toLowerCase().includes(term)
        );
      }
      return list;
    };

    const pages = selectedFiches.map((fiche, idx) => {
      const ficheStudents = studentsForFiche(fiche);
      const tableHTML = modelType === 'detailed' ? buildDetailedTableHTML(ficheStudents) : buildSimpleTableHTML(ficheStudents);
      const metaBlock = `
        <div style="display:flex;justify-content:space-between;flex-wrap:wrap;gap:4px;margin-bottom:8px;font-size:10px;">
          <div><strong>${t.academicYear}:</strong> ${yearLabel}</div>
          <div><strong>${t.classLabel}:</strong> ${fiche.class_name || clsName || '—'}</div>
          <div><strong>${t.teacher}:</strong> ${fiche.teacher || '—'}</div>
          <div><strong>${t.subject}:</strong> ${fiche.subject || '—'}</div>
          <div><strong>${t.coefficient}:</strong> ${fiche.coefficient || '—'}</div>
          ${selectedFiches.length > 1 ? `<div style="color:#1e3a5f;font-weight:bold;">${t.fiche} ${idx + 1}/${selectedFiches.length}</div>` : ''}
        </div>`;
      return `<div style="page-break-after:${idx < fiches.length - 1 ? 'always' : 'auto'};">
        ${headerBlock}
        <h1 style="text-align:center;font-size:20px;font-weight:bold;color:#1e3a5f;margin:10px 0;text-transform:uppercase;letter-spacing:1px;">${t.listTitle}${modelType === 'detailed' ? ' — CC/EU/SEQ' : ''}</h1>
        ${metaBlock}
        ${tableHTML}
        <div class="footer">${schoolHeader.school_name} — ${t.title} — ${today}${fiche.class_name ? ` — ${fiche.class_name}` : ''}${fiche.teacher ? ` — ${fiche.teacher}` : ''}${fiche.subject ? ` — ${fiche.subject}` : ''}</div>
      </div>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${t.title}</title><style>
@page{size:A4 portrait;margin:10mm}
body{font-family:Arial,sans-serif;margin:0;padding:8px;font-size:10px}
table{width:100%;border-collapse:collapse}
td,th{border:1px solid #666 !important}
thead{display:table-header-group}
tr{page-break-inside:avoid}
.footer{margin-top:12px;text-align:center;font-size:8px;color:#999;border-top:1px solid #eee;padding-top:6px}
@media print{
  .no-print{display:none}
  *{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  td,th{border:1.2px solid #444 !important}
}
</style></head><body>
      ${pages}
      <div class="no-print" style="text-align:center;margin-top:12px;"><button onclick="window.print()" style="padding:8px 24px;background:#1e3a5f;color:#fff;border:none;border-radius:4px;cursor:pointer;">${lang === 'fr' ? 'Imprimer' : 'Print'} (${selectedFiches.length})</button></div>
    </body></html>`;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  useEffect(() => {
    const yearId = getSelectedYearId();
    const yearLabel = localStorage.getItem(SELECTED_YEAR_LABEL_KEY);
    if (yearId) {
      setSelectedAcademicYearId(yearId);
      if (yearLabel) setCurrentAcademicYear({ id: yearId, name: yearLabel });
    }
    try {
      const storedSchool = localStorage.getItem('lybibal-selected-school-id');
      if (storedSchool) setSchoolId(Number(storedSchool));
    } catch (err) { /* ignore */ }
  }, []);

  useEffect(() => {
    const handleYearChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.year_id) {
        setSelectedAcademicYearId(Number(detail.year_id));
        if (detail.label) setCurrentAcademicYear({ id: Number(detail.year_id), name: detail.label });
        setSelectedClass("");
        setAllStudents([]);
      }
    };
    window.addEventListener(SELECTED_YEAR_EVENT, handleYearChange);
    return () => window.removeEventListener(SELECTED_YEAR_EVENT, handleYearChange);
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('lybibal-school-header');
      if (saved) setSchoolHeader(JSON.parse(saved));
    } catch { /* ignore */ }
  }, []);

  if (!selectedAcademicYearId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center max-w-md">
          <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t.noYearSelected}</h2>
          <p className="text-sm text-gray-500">{t.yearRequired}</p>
        </div>
      </div>
    );
  }

  // ─── RENDU DU TABLEAU SIMPLE ───
  const renderSimpleTable = () => (
    <table className="w-full">
      <thead className="bg-[#1e3a5f] text-white">
        <tr>
          <th className="px-2 py-2 text-center text-xs font-semibold w-8">{t.no}</th>
          <th className="px-2 py-2 text-left text-xs font-semibold w-24">{t.matricule}</th>
          <th className="px-2 py-2 text-center text-xs font-semibold w-8">{t.sex}</th>
          <th className="px-2 py-2 text-left text-xs font-semibold">{t.name}</th>
          <th className="px-2 py-2 text-center text-xs font-semibold w-12">{t.seq1}</th>
          <th className="px-2 py-2 text-center text-xs font-semibold w-12">{t.seq2}</th>
          <th className="px-2 py-2 text-center text-xs font-semibold w-12">{t.seq3}</th>
          <th className="px-2 py-2 text-center text-xs font-semibold w-12">{t.seq4}</th>
          <th className="px-2 py-2 text-center text-xs font-semibold w-12">{t.seq5}</th>
          <th className="px-2 py-2 text-center text-xs font-semibold w-12">{t.seq6}</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {loading || refLoading ? (
          <tr><td colSpan={10} className="px-4 py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /><p className="text-sm text-gray-500 mt-2">{t.loading}</p></td></tr>
        ) : displayStudents.length === 0 ? (
          <tr><td colSpan={10} className="px-4 py-12 text-center text-gray-500"><GraduationCap className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p className="text-sm">{t.noData}</p></td></tr>
        ) : (
          displayStudents.map((s, i) => (
            <tr key={s.id} className="hover:bg-gray-50 transition-colors">
              <td className="px-2 py-1.5 text-center text-gray-500 text-xs">{i + 1}</td>
              <td className="px-2 py-1.5 font-mono text-gray-600 text-xs">{s.matricule || '—'}</td>
              <td className="px-2 py-1.5 text-center text-gray-600 text-xs">{s.gender || '—'}</td>
              <td className="px-2 py-1.5 font-medium text-gray-900 text-xs">{s.last_name} {s.first_name}</td>
              {(['seq1', 'seq2', 'seq3', 'seq4', 'seq5', 'seq6'] as const).map(seq => (
                <td key={seq} className="px-1 py-1.5 text-center">
                  {editing ? (
                    <input type="number" min="0" max="20" step="0.5" value={s[seq] ?? ''} onChange={e => updateGrade(s.id, seq, e.target.value)} className="w-10 px-1 py-0.5 text-center text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[#1e3a8a]" />
                  ) : (
                    <span className="text-xs text-gray-700">{s[seq] ?? '—'}</span>
                  )}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );

  // ─── RENDU DU TABLEAU DÉTAILLÉ ───
  const renderDetailedTable = () => (
    <table className="w-full border-collapse">
      <thead>
        <tr className="bg-[#1e3a5f] text-white">
          <th rowSpan={2} className="px-1 py-2 text-center text-[10px] font-semibold border border-[#2d4a6f] w-16">{t.matricule}</th>
          <th rowSpan={2} className="px-1 py-2 text-center text-[10px] font-semibold border border-[#2d4a6f] w-8">{t.sex}</th>
          <th rowSpan={2} className="px-1 py-2 text-left text-[10px] font-semibold border border-[#2d4a6f] min-w-[140px]">{t.name}</th>
          <th colSpan={3} className="px-1 py-1.5 text-center text-[10px] font-semibold border border-[#2d4a6f]">{t.seq1}</th>
          <th colSpan={3} className="px-1 py-1.5 text-center text-[10px] font-semibold border border-[#2d4a6f]">{t.seq2}</th>
          <th colSpan={3} className="px-1 py-1.5 text-center text-[10px] font-semibold border border-[#2d4a6f]">{t.seq3}</th>
          <th colSpan={3} className="px-1 py-1.5 text-center text-[10px] font-semibold border border-[#2d4a6f]">{t.seq4}</th>
          <th colSpan={3} className="px-1 py-1.5 text-center text-[10px] font-semibold border border-[#2d4a6f]">{t.seq5}</th>
          <th rowSpan={2} className="px-1 py-2 text-center text-[10px] font-semibold border border-[#2d4a6f] w-10">{t.general}</th>
        </tr>
        <tr className="bg-[#2d4a6f] text-white">
          <th className="px-0.5 py-1 text-center text-[9px] font-medium border border-[#3d5a7f]">{t.cc}1</th>
          <th className="px-0.5 py-1 text-center text-[9px] font-medium border border-[#3d5a7f]">{t.eu}1</th>
          <th className="px-0.5 py-1 text-center text-[9px] font-bold border border-[#3d5a7f]">{t.seq}1</th>
          <th className="px-0.5 py-1 text-center text-[9px] font-medium border border-[#3d5a7f]">{t.cc}2</th>
          <th className="px-0.5 py-1 text-center text-[9px] font-medium border border-[#3d5a7f]">{t.eu}2</th>
          <th className="px-0.5 py-1 text-center text-[9px] font-bold border border-[#3d5a7f]">{t.seq}2</th>
          <th className="px-0.5 py-1 text-center text-[9px] font-medium border border-[#3d5a7f]">{t.cc}3</th>
          <th className="px-0.5 py-1 text-center text-[9px] font-medium border border-[#3d5a7f]">{t.eu}3</th>
          <th className="px-0.5 py-1 text-center text-[9px] font-bold border border-[#3d5a7f]">{t.seq}3</th>
          <th className="px-0.5 py-1 text-center text-[9px] font-medium border border-[#3d5a7f]">{t.cc}4</th>
          <th className="px-0.5 py-1 text-center text-[9px] font-medium border border-[#3d5a7f]">{t.eu}4</th>
          <th className="px-0.5 py-1 text-center text-[9px] font-bold border border-[#3d5a7f]">{t.seq}4</th>
          <th className="px-0.5 py-1 text-center text-[9px] font-medium border border-[#3d5a7f]">{t.cc}5</th>
          <th className="px-0.5 py-1 text-center text-[9px] font-medium border border-[#3d5a7f]">{t.eu}5</th>
          <th className="px-0.5 py-1 text-center text-[9px] font-bold border border-[#3d5a7f]">{t.seq}5</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {loading || refLoading ? (
          <tr><td colSpan={19} className="px-4 py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /><p className="text-sm text-gray-500 mt-2">{t.loading}</p></td></tr>
        ) : displayStudents.length === 0 ? (
          <tr><td colSpan={19} className="px-4 py-12 text-center text-gray-500"><GraduationCap className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p className="text-sm">{t.noData}</p></td></tr>
        ) : (
          displayStudents.map((s) => (
            <tr key={s.id} className="hover:bg-gray-50 transition-colors">
              <td className="px-1 py-1.5 text-center text-gray-500 text-[10px] font-mono border border-gray-100">{s.matricule || '—'}</td>
              <td className="px-1 py-1.5 text-center text-gray-600 text-[10px] border border-gray-100">{s.gender || '—'}</td>
              <td className="px-1 py-1.5 font-medium text-gray-900 text-[10px] border border-gray-100">{s.last_name} {s.first_name}</td>
              {([1,2,3,4,5] as const).map(seqNum => {
                const ccKey = `cc${seqNum}` as keyof Student;
                const euKey = `eu${seqNum}` as keyof Student;
                const sKey = `s${seqNum}` as keyof Student;
                return (
                  <React.Fragment key={seqNum}>
                    <td className="px-0.5 py-1.5 text-center border border-gray-100">
                      {editing ? (
                        <input type="number" min="0" max="20" step="0.5" value={(s[ccKey] as number | undefined) ?? ''} onChange={e => updateGrade(s.id, ccKey, e.target.value)} className="w-8 px-0.5 py-0.5 text-center text-[10px] border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[#1e3a8a]" />
                      ) : (
                        <span className="text-[10px] text-gray-600">{(s[ccKey] as number | undefined) ?? '—'}</span>
                      )}
                    </td>
                    <td className="px-0.5 py-1.5 text-center border border-gray-100">
                      {editing ? (
                        <input type="number" min="0" max="20" step="0.5" value={(s[euKey] as number | undefined) ?? ''} onChange={e => updateGrade(s.id, euKey, e.target.value)} className="w-8 px-0.5 py-0.5 text-center text-[10px] border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[#1e3a8a]" />
                      ) : (
                        <span className="text-[10px] text-gray-600">{(s[euKey] as number | undefined) ?? '—'}</span>
                      )}
                    </td>
                    <td className="px-0.5 py-1.5 text-center border border-gray-100 bg-blue-50/30">
                      {editing ? (
                        <input type="number" min="0" max="20" step="0.5" value={(s[sKey] as number | undefined) ?? ''} onChange={e => updateGrade(s.id, sKey, e.target.value)} className="w-8 px-0.5 py-0.5 text-center text-[10px] border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[#1e3a8a] font-semibold" />
                      ) : (
                        <span className="text-[10px] text-[#1e3a8a] font-bold">{(s[sKey] as number | undefined) ?? '—'}</span>
                      )}
                    </td>
                  </React.Fragment>
                );
              })}
              <td className="px-1 py-1.5 text-center font-bold text-[10px] text-[#1e3a8a] border border-gray-100">{calculateGeneral(s, modelType)}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <GraduationCap className="w-7 h-7 text-[#1e3a8a]" />
          {t.title}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{t.subtitle}</p>
        {currentAcademicYear && (
          <div className="flex items-center gap-2 mt-2">
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
              {currentAcademicYear.name}
            </span>
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full flex items-center gap-1">
              {modelType === 'simple' ? <Table2 className="w-3 h-3" /> : <LayoutTemplate className="w-3 h-3" />}
              {modelType === 'simple' ? t.modelSimple : t.modelDetailed}
            </span>
            {(clsName || fiches.length > 0) && (
              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                {selectedFiches.length}/{fiches.length} {t.ficheCount}{clsName ? ` — ${clsName}` : selectedLevel ? ` — ${levels.find((l: any) => String(l.id) === selectedLevel)?.name || ''} (${classes.length} ${lang === 'fr' ? 'classes' : 'classes'})` : ''}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* LEFT PANEL */}
        <div className="xl:col-span-1 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Settings className="w-4 h-4 text-[#1e3a8a]" />
              {t.configTitle}
            </h3>
            <div className="space-y-3">
              {/* Sélecteur de modèle */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.modelType}</label>
                <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                  <button
                    onClick={() => setModelType('simple')}
                    className={`flex-1 px-3 py-2 text-xs font-medium flex items-center justify-center gap-1 transition-colors ${
                      modelType === 'simple' ? 'bg-[#1e3a8a] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Table2 className="w-3 h-3" /> {t.modelSimple}
                  </button>
                  <button
                    onClick={() => setModelType('detailed')}
                    className={`flex-1 px-3 py-2 text-xs font-medium flex items-center justify-center gap-1 transition-colors ${
                      modelType === 'detailed' ? 'bg-[#1e3a8a] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <LayoutTemplate className="w-3 h-3" /> {t.modelDetailed}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.academicYear}</label>
                <select value={selectedAcademicYearId ?? ''} onChange={(e) => setSelectedAcademicYearId(e.target.value ? Number(e.target.value) : null)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]">
                  <option value="">{t.academicYear}</option>
                  {years.map((y: any) => <option key={y.id} value={String(y.id)}>{y.label || y.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.section}</label>
                <select value={selectedSection} onChange={(e) => { setSelectedSection(e.target.value); setSelectedCycle(""); setSelectedLevel(""); setSelectedClass(""); setClasses([]); }} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]">
                  <option value="">{t.allSections}</option>
                  {sections.map((s: any) => <option key={s.id} value={String(s.id)}>{s.name || s.label_fr}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.cycle}</label>
                <select value={selectedCycle} onChange={(e) => { setSelectedCycle(e.target.value); setSelectedLevel(""); setSelectedClass(""); setClasses([]); }} disabled={!selectedSection} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] disabled:bg-gray-100 disabled:cursor-not-allowed">
                  <option value="">{t.allCycles}</option>
                  {cycles.filter((c: any) => !selectedSection || String(c.section_id) === selectedSection).map((c: any) => <option key={c.id} value={String(c.id)}>{c.name || c.label_fr}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.level}</label>
                <select value={selectedLevel} onChange={(e) => { setSelectedLevel(e.target.value); setSelectedClass(""); setClasses([]); }} disabled={!selectedCycle} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] disabled:bg-gray-100 disabled:cursor-not-allowed">
                  <option value="">{t.allLevels}</option>
                  {levels.filter((l: any) => !selectedCycle || String(l.cycle_id) === selectedCycle).map((l: any) => <option key={l.id} value={String(l.id)}>{l.name || l.label_fr}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.class}</label>
                <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)} disabled={!selectedLevel} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] disabled:bg-gray-100 disabled:cursor-not-allowed">
                  <option value="">{t.allClasses}{selectedLevel ? ` (${classes.length} — ${lang === 'fr' ? 'tout le niveau' : 'whole level'})` : ''}</option>
                  {classes.map((c: any) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                </select>
                {selectedLevel && classes.length === 0 && <p className="text-xs text-orange-600 mt-1">{lang === 'fr' ? 'Aucune classe trouvée pour ce niveau.' : 'No classes found for this level.'}</p>}
              </div>

              {/* ── Zone enseignant/matière : auto depuis les attributions,
                  sinon saisie manuelle (secours) ── */}
              {selectedClass && assignmentsLoading && (
                <div className="flex items-center gap-2 text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-[#1e3a8a]" />
                  {t.loadingAssignments}
                </div>
              )}
              {selectedClass && !assignmentsLoading && assignments.length > 0 && (
                <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                  <p className="text-xs font-semibold text-emerald-800 flex items-center gap-1.5 mb-2">
                    <UserRound className="w-3.5 h-3.5" />
                    {t.autoMode} — {assignments.length} {t.ficheCount}
                  </p>
                  <ul className="space-y-1.5 max-h-40 overflow-y-auto">
                    {assignments.map((a, i) => (
                      <li key={i} className="text-xs text-emerald-900 flex items-start gap-1.5">
                        <BookOpen className="w-3 h-3 mt-0.5 shrink-0 text-emerald-600" />
                        <span>
                          <strong>{a.teacher || '—'}</strong>
                          {a.subject ? <> — {a.subject}</> : null}
                          {a.coefficient ? <span className="text-emerald-600"> ({t.coefficient} {a.coefficient})</span> : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {selectedClass && !assignmentsLoading && assignments.length === 0 && (
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                  <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5 mb-1.5">
                    <Pencil className="w-3.5 h-3.5" />
                    {t.manualMode}
                  </p>
                  <p className="text-xs text-amber-700">{t.noAssignments}</p>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.teacher}</label>
                <input type="text" value={teacher} onChange={e => setTeacher(e.target.value)} disabled={assignments.length > 0} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] disabled:bg-gray-100 disabled:cursor-not-allowed" placeholder={lang === 'fr' ? "Nom de l'enseignant" : 'Teacher name'} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.subject}</label>
                <input type="text" value={subject} onChange={e => setSubject(e.target.value)} disabled={assignments.length > 0} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] disabled:bg-gray-100 disabled:cursor-not-allowed" placeholder={lang === 'fr' ? 'Matière' : 'Subject'} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.coefficient}</label>
                <input type="text" value={coefficient} onChange={e => setCoefficient(e.target.value)} disabled={assignments.length > 0} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] disabled:bg-gray-100 disabled:cursor-not-allowed" placeholder="1, 2, 3..." />
              </div>
            </div>
          </div>

          {/* School Header Config */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <button onClick={() => setShowHeaderConfig(!showHeaderConfig)} className="flex items-center justify-between w-full text-sm font-semibold text-gray-900">
              <span className="flex items-center gap-2"><Building2 className="w-4 h-4 text-[#1e3a8a]" />{t.headerTitle}</span>
              {showHeaderConfig ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showHeaderConfig && (
              <div className="mt-4 space-y-3">
                <p className="text-xs text-gray-500">{t.headerDesc}</p>
                <div><label className="block text-xs text-gray-600 mb-1">{t.schoolName}</label><input value={schoolHeader.school_name} onChange={(e) => setSchoolHeader(p => ({ ...p, school_name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.schoolNameEn}</label><input value={schoolHeader.school_name_en} onChange={(e) => setSchoolHeader(p => ({ ...p, school_name_en: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.region}</label><input value={schoolHeader.region} onChange={(e) => setSchoolHeader(p => ({ ...p, region: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.department}</label><input value={schoolHeader.department} onChange={(e) => setSchoolHeader(p => ({ ...p, department: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.delegation}</label><input value={schoolHeader.delegation} onChange={(e) => setSchoolHeader(p => ({ ...p, delegation: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.subDivision}</label><input value={schoolHeader.sub_division} onChange={(e) => setSchoolHeader(p => ({ ...p, sub_division: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.address}</label><input value={schoolHeader.address} onChange={(e) => setSchoolHeader(p => ({ ...p, address: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.poBox}</label><input value={schoolHeader.po_box} onChange={(e) => setSchoolHeader(p => ({ ...p, po_box: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t.logo}</label>
                  <input type="file" accept="image/*" onChange={(e) => { const file = e.target.files?.[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => setSchoolHeader(p => ({ ...p, logo_url: reader.result as string })); reader.readAsDataURL(file); } }} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                  {schoolHeader.logo_url && (
                    <div className="mt-2 flex items-center gap-2">
                      <img src={schoolHeader.logo_url} alt="Logo" className="w-12 h-12 object-contain border rounded" />
                      <button onClick={() => setSchoolHeader(p => ({ ...p, logo_url: '' }))} className="text-xs text-red-600 hover:text-red-800 underline">{lang === 'fr' ? 'Supprimer' : 'Remove'}</button>
                    </div>
                  )}
                </div>
                <button onClick={handleSaveHeader} className="w-full px-4 py-2 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] flex items-center justify-center gap-2"><Save className="w-4 h-4" />{t.saveHeader}</button>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="xl:col-span-2 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input type="text" placeholder={t.search} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] w-full" />
            </div>
            <button onClick={() => setEditing(!editing)} className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${editing ? 'bg-amber-100 text-amber-700 border border-amber-200' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              <Pencil className="w-4 h-4" />{editing ? (lang === 'fr' ? 'Terminer' : 'Done') : (lang === 'fr' ? 'Modifier' : 'Edit')}
            </button>
            {editing && (
              <button onClick={handleSave} className="px-4 py-2 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] flex items-center gap-2 transition-colors">
                <Save className="w-4 h-4" />{t.save}
              </button>
            )}
            <div className="h-6 w-px bg-gray-200 hidden sm:block" />
            <button onClick={handlePrintPDF} className="px-4 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 flex items-center gap-2 transition-colors"><Printer className="w-4 h-4" />{t.exportPDF}{selectedFiches.length > 1 ? ` (${selectedFiches.length})` : ''}</button>
            <button onClick={handleExportCSV} className="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-100 flex items-center gap-2 transition-colors"><FileSpreadsheet className="w-4 h-4" />{t.exportExcel}{selectedFiches.length > 1 ? ` (${selectedFiches.length})` : ''}</button>
            <button onClick={handleExportExcelData} className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 flex items-center gap-2 transition-colors"><Download className="w-4 h-4" />{t.exportExcelData}</button>
          </div>

          {/* ── Sélecteur de fiche (une par enseignant/matière) + méta ── */}
          {fiches.length > 1 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3">
              <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1.5">
                <UserRound className="w-3.5 h-3.5 text-[#1e3a8a]" />
                {t.fiches} — {fiches.length} {t.ficheCount}
              </p>
              <div className="flex items-center gap-2 mb-2">
                <button
                  onClick={() => setExcludedKeys(new Set())}
                  className="text-[11px] px-2 py-1 bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  {lang === 'fr' ? '☑ Tout cocher' : '☑ Check all'}
                </button>
                <button
                  onClick={() => setExcludedKeys(new Set(fiches.map(ficheKey)))}
                  className="text-[11px] px-2 py-1 bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  {lang === 'fr' ? '☐ Tout décocher' : '☐ Uncheck all'}
                </button>
                <span className="text-[11px] text-gray-400 ml-auto">
                  {selectedFiches.length}/{fiches.length} {lang === 'fr' ? 'sélectionnée(s)' : 'selected'}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {fiches.map((f, i) => {
                  const checked = !excludedKeys.has(ficheKey(f));
                  return (
                    <div
                      key={i}
                      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border transition-colors ${
                        checked
                          ? (i === activeFiche ? 'bg-[#1e3a8a] text-white border-[#1e3a8a]' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50')
                          : 'bg-gray-50 text-gray-400 border-gray-100 opacity-60'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleFiche(f)}
                        className="w-3.5 h-3.5 shrink-0 cursor-pointer"
                        title={lang === 'fr' ? 'Inclure / exclure cette fiche' : 'Include / exclude this form'}
                      />
                      <button
                        type="button"
                        onClick={() => setActiveFiche(i)}
                        className="flex items-center gap-1.5 text-[11px] font-medium"
                      >
                        {f.class_name && <span className="font-bold">{f.class_name}</span>}
                        {f.class_name && <span className={i === activeFiche && checked ? 'text-blue-200' : 'text-gray-300'}>·</span>}
                        <span className="font-semibold">{f.teacher || '—'}</span>
                        {f.subject && <span className={i === activeFiche && checked ? 'text-blue-200' : 'text-gray-400'}>• {f.subject}</span>}
                        {f.coefficient && <span className={i === activeFiche && checked ? 'text-blue-200' : 'text-gray-400'}>• {t.coefficient} {f.coefficient}</span>}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Ligne méta de la fiche active ── */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-4 py-2.5 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-gray-600">
            <span><span className="font-semibold text-gray-800">{t.classLabel}:</span> {activeMeta.class_name || clsName || '—'}</span>
            <span className="flex items-center gap-1"><UserRound className="w-3 h-3 text-gray-400" /><span className="font-semibold text-gray-800">{t.teacher}:</span> {activeMeta.teacher || '—'}</span>
            <span className="flex items-center gap-1"><BookOpen className="w-3 h-3 text-gray-400" /><span className="font-semibold text-gray-800">{t.subject}:</span> {activeMeta.subject || '—'}</span>
            <span><span className="font-semibold text-gray-800">{t.coefficient}:</span> {activeMeta.coefficient || '—'}</span>
            <span className="font-semibold text-gray-800">{t.academicYear}:</span> {yearLabel}
            {fiches.length > 1 && (
              <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-semibold">{activeFiche + 1}/{fiches.length}</span>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              {modelType === 'simple' ? renderSimpleTable() : renderDetailedTable()}
            </div>
            {displayStudents.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                <span className="text-xs text-gray-500">{displayStudents.length} {lang === 'fr' ? 'élève(s)' : 'student(s)'}</span>
                <span className="text-xs text-gray-500">{t.classLabel}: {clsName || (lang === 'fr' ? 'Toutes' : 'All')}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Popup d'information : aucune attribution pour cette classe ── */}
      {showNoAssignPopup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={() => setShowNoAssignPopup(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 relative animate-in fade-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowNoAssignPopup(false)}
              className="absolute top-3 right-3 w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors"
              aria-label={t.popupClose}
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full bg-amber-50 border border-amber-100 flex items-center justify-center mb-4">
                <Info className="w-7 h-7 text-amber-500" />
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-2">{t.popupTitle}</h3>
              <p className="text-sm text-gray-500 leading-relaxed mb-5">{t.noAssignments}</p>
              <button
                onClick={() => setShowNoAssignPopup(false)}
                className="w-full px-4 py-2.5 bg-[#1e3a8a] text-white rounded-xl text-sm font-medium hover:bg-[#152a5e] transition-colors"
              >
                {t.popupManual}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function headerEnRegion(fr: string): string {
  return fr.replace("RÉGION DE L'", "WEST ").replace("RÉGION", "REGION");
}
function headerEnDept(fr: string): string {
  return fr.replace("DÉPARTEMENT DE LA ", "").replace("DÉPARTEMENT", "DIVISION");
}
function headerEnDelegation(fr: string): string {
  const map: Record<string, string> = {
    'DÉLÉGATION DÉPARTEMENTALE DES ENSEIGNEMENTS SECONDAIRES': 'DIVISIONAL DELEGATION FOR SECONDARY EDUCATION',
  };
  return map[fr] || fr;
}